import { EventType } from "@/analytics/analytics-events";
import { useCurrentProjectApiClient } from "@/components/common/project-provider/project-loading-context";
import { updateProject } from "@/components/common/project-provider/update-project";
import { ColormapOptionsForms } from "@/components/r3f/renderers/analyses/colormap-options-forms";
import { ColormapReferencePlanePanel } from "@/components/r3f/renderers/analyses/colormap-reference-plane-panel";
import { ColormapScale } from "@/components/r3f/renderers/analyses/colormap-scale";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useCanChangeTool } from "@/hooks/use-can-change-tool";
import { useSaveAnalysisAction } from "@/hooks/use-save-analysis-action";
import { useShareAnnotationOrAnalysis } from "@/hooks/use-share-annotation-or-analysis";
import { useCurrentArea } from "@/modes/mode-data-context";
import { PointCloudObject } from "@/object-cache";
import {
  selectAnalysisIsDirty,
  selectLastCreatedAnalysisId,
} from "@/store/point-cloud-analysis-tool-selector";
import {
  PointCloudAnalysis,
  removeAnalysis,
  setAnalysisVisibility,
  setLastCreatedAnalysisId,
} from "@/store/point-cloud-analysis-tool-slice";
import { selectAnnotationSection } from "@/store/selections-selectors";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import {
  activateTool,
  deactivateTool,
  setShowSpinner,
  ToolName,
} from "@/store/ui/ui-slice";
import { selectHasWritePermission } from "@/store/user-selectors";
import { selectObjectVisibility } from "@/store/view-options/view-options-selectors";
import {
  setObjectVisibility,
  ViewObjectTypes,
} from "@/store/view-options/view-options-slice";
import {
  AddLabelIcon,
  ArrowDownIcon,
  CloseCancelErrorIcon,
  CloudUploadIcon,
  cyan,
  FaroIconButton,
  FaroText,
  grey,
  ShareLinkIcon,
  useDialog,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import { selectIElement } from "@faro-lotv/project-source";
import { createMutationDeleteElement } from "@faro-lotv/service-wires";
import { Stack, Tooltip } from "@mui/material";
import { Box } from "@mui/system";
import { useCallback, useEffect, useState, type JSX } from "react";
import { DeleteElementDialogContent } from "../delete-element-dialog-content";
import {
  EmbeddedToolbarButton,
  EmbeddedToolbarVerticalDivider,
} from "../embedded-toolbar";
import { AnalysisViewerMenu } from "./analysis-viewer-menu";
import { CreatePdfReportDialog } from "./create-pdf-report-dialog";
import { PdfReportData, useSavePdfReport } from "./use-save-pdf-report";

const toolbarButtonStyle = {
  p: 0,
  border: 0,
  ":hover *": {
    color: `${cyan[400]} !important`,
  },
};

const toolbarIconStyle = {
  height: 20,
  width: 20,
};

export type AnalysisDockingToolbarProps = {
  /** The point cloud object used by the analysis */
  pointCloud: PointCloudObject;
  /** The point cloud analysis being edited */
  analysis: PointCloudAnalysis;
};

/** @returns a PopOver to open from a menu to control the colormap view options */
export function AnalysisDockingToolbar({
  pointCloud,
  analysis,
}: AnalysisDockingToolbarProps): JSX.Element {
  const appStore = useAppStore();
  const dispatch = useAppDispatch();
  const client = useCurrentProjectApiClient();
  const { handleErrorWithToast } = useErrorHandlers();
  const activeTool = useAppSelector(selectActiveTool);
  const { area } = useCurrentArea();
  const analysisElement = useAppSelector(selectIElement(analysis.id));
  const saveAnalysis = useSaveAnalysisAction(analysis);
  const isAnalysisDirty = useAppSelector(selectAnalysisIsDirty(analysis.id));
  // If the user has permission to edit the project or not
  const hasWritePermission = useAppSelector(selectHasWritePermission);

  const addLabels = useCallback(() => {
    if (activeTool === ToolName.analysisLabel) {
      dispatch(deactivateTool());
    } else {
      Analytics.track(EventType.openColoredAnalysisLabelCreationMode);
      dispatch(activateTool(ToolName.analysisLabel));
    }
  }, [activeTool, dispatch]);

  const hideAnalysis = useCallback(() => {
    Analytics.track(EventType.hideColoredAnalysis);

    dispatch(
      setAnalysisVisibility({
        analysisId: analysis.id,
        visible: false,
      }),
    );
    dispatch(deactivateTool());
    dispatch(setLastCreatedAnalysisId(undefined));
  }, [analysis.id, dispatch]);

  const shareAnalysis = useShareAnnotationOrAnalysis(analysisElement?.id, true);

  const { createDialog } = useDialog();

  const deleteActiveAnalysis = useCallback(async () => {
    const shouldDelete = await createDialog({
      title: "Delete Analysis?",
      confirmText: "Delete",
      content: <DeleteElementDialogContent name="the analysis" dark />,
      variant: "danger",
      dark: true,
    });

    if (!shouldDelete) {
      return;
    }

    Analytics.track(EventType.deleteColoredAnalysis);

    dispatch(setShowSpinner(true));
    const analysisElement = selectIElement(analysis.id)(appStore.getState());
    try {
      if (analysisElement) {
        const cloudElement = selectIElement(analysis.parentId)(
          appStore.getState(),
        );
        assert(
          cloudElement,
          "There must be point cloud associated to the point cloud analysis",
        );
        const annotationSection = selectAnnotationSection(
          cloudElement,
          area,
        )(appStore.getState());
        assert(annotationSection, "There must be parent annotation section");
        await client.applyMutations([
          createMutationDeleteElement(analysisElement.id),
        ]);
        // update local copy of the project
        dispatch(
          updateProject({
            projectApi: client,
            iElementQuery: {
              ancestorIds: [annotationSection.id],
            },
          }),
        );
      }
      dispatch(removeAnalysis(analysis.id));
    } catch (error) {
      handleErrorWithToast({
        title: "Failed to delete the point cloud analysis",
        error,
      });
    }
    dispatch(setShowSpinner(false));
  }, [
    analysis.id,
    analysis.parentId,
    appStore,
    area,
    client,
    dispatch,
    handleErrorWithToast,
    createDialog,
  ]);

  const lastCreatedAnalysisId = useAppSelector(selectLastCreatedAnalysisId);
  const [editMode, setEditMode] = useState(
    lastCreatedAnalysisId === analysis.id,
  );

  const [reportData, setReportData] = useState<
    PdfReportData | string | undefined
  >();

  const [measurementVisible, setMeasurementVisible] = useState(false);
  const [isCreateReportDialogOpen, setIsCreateReportDialogOpen] =
    useState(false);

  const onCloseCreateReportDialog = useCallback(() => {
    dispatch(
      setObjectVisibility({
        type: ViewObjectTypes.measurements,
        visibility: measurementVisible,
      }),
    );
    setIsCreateReportDialogOpen(false);
  }, [dispatch, measurementVisible]);

  const savePdfReport = useSavePdfReport();

  // Save PDF report when reportImage or reportText changes
  useEffect(() => {
    async function saveReport(): Promise<void> {
      if (!reportData || typeof reportData === "string") {
        handleErrorWithToast({
          title: "Error saving PDF report",
          error: reportData ?? "Undefined error",
        });
        return;
      }
      dispatch(setShowSpinner(true));
      try {
        assert(
          reportData.imageDataUrl &&
            reportData.title &&
            reportData.analysisType,
          "report title and image are mandatory",
        );

        await savePdfReport(reportData);
      } catch (error) {
        handleErrorWithToast({
          title: "Error saving PDF report",
          error,
        });
      } finally {
        dispatch(setShowSpinner(false));
      }
    }
    if (reportData) {
      // Let React update the DOM and show the spinner before heavy work of report saving
      setTimeout(() => {
        saveReport();
      }, 0);
    }
  }, [dispatch, handleErrorWithToast, reportData, savePdfReport]);

  const canChangeTool = useCanChangeTool();

  return (
    <Box
      component="div"
      sx={{
        padding: 1.5,
        position: "relative",
        borderRadius: 1,
        backgroundColor: grey[1000],
      }}
    >
      <Stack sx={{ gap: 2, height: "100%" }}>
        <Stack
          direction="row"
          sx={{
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" sx={{ width: "100%", alignItems: "center" }}>
            {/* Title */}
            <FaroText
              variant="labelS"
              sx={{ color: grey[0], fontWeight: "bold" }}
            >
              {editMode ? "ANALYSIS" : ""}
            </FaroText>
            <FaroIconButton
              size="s"
              margin={0}
              color={grey[0]}
              hoverColor={cyan[400]}
              sx={{ ml: "3px" }}
              onClick={() => {
                setEditMode(!editMode);
              }}
              title={editMode ? "Collapse" : "Expand"}
            >
              <ArrowDownIcon
                sx={{
                  transform: editMode ? "rotate(-90deg)" : "rotate(90deg)",
                  transition: "transform 0.1s linear",
                }}
              />
            </FaroIconButton>
          </Stack>
          {/* Edit mode toolbar */}
          {editMode && (
            <>
              <Stack
                direction="row"
                sx={{ gap: 0.5, justifyContent: "flex-end" }}
              >
                <Tooltip title="Add labels" placement="top">
                  <EmbeddedToolbarButton
                    aria-label="add labels"
                    value="Add labels"
                    selected={activeTool === ToolName.analysisLabel}
                    onClick={addLabels}
                    sx={toolbarButtonStyle}
                    disabled={!canChangeTool}
                  >
                    <AddLabelIcon sx={toolbarIconStyle} />
                  </EmbeddedToolbarButton>
                </Tooltip>
                {isAnalysisDirty && (
                  <Tooltip
                    placement="top"
                    title={
                      hasWritePermission
                        ? "Save"
                        : "Saving point cloud analysis is only accessible with edit permissions"
                    }
                  >
                    <EmbeddedToolbarButton
                      aria-label="save analysis"
                      value="Save"
                      disabled={!hasWritePermission}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        saveAnalysis();
                      }}
                      sx={toolbarButtonStyle}
                    >
                      <CloudUploadIcon sx={toolbarIconStyle} />
                    </EmbeddedToolbarButton>
                  </Tooltip>
                )}
                {!isAnalysisDirty && (
                  <Tooltip placement="top" title="Share">
                    <EmbeddedToolbarButton
                      aria-label="share analysis"
                      value="Share"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        shareAnalysis();
                      }}
                      sx={toolbarButtonStyle}
                    >
                      <ShareLinkIcon sx={toolbarIconStyle} />
                    </EmbeddedToolbarButton>
                  </Tooltip>
                )}
              </Stack>

              <EmbeddedToolbarVerticalDivider
                sx={{
                  backgroundColor: grey[800],
                  ml: 1,
                  mr: 1,
                  height: "16px",
                  opacity: 1,
                }}
              />
            </>
          )}

          <AnalysisViewerMenu
            editMode={editMode}
            isAnalysisDirty={isAnalysisDirty ?? false}
            analysisElement={analysisElement}
            onDelete={deleteActiveAnalysis}
            onShare={shareAnalysis}
            onCreateReport={() => {
              const areMeasurementVisible = selectObjectVisibility(
                ViewObjectTypes.measurements,
              )(appStore.getState());
              setMeasurementVisible(areMeasurementVisible);
              dispatch(
                setObjectVisibility({
                  type: ViewObjectTypes.measurements,
                  visibility: false,
                }),
              );

              setIsCreateReportDialogOpen(true);
            }}
            onSave={saveAnalysis}
          />
          {isCreateReportDialogOpen && (
            <CreatePdfReportDialog
              pointCloud={pointCloud}
              analysis={analysis}
              open={isCreateReportDialogOpen}
              onClose={onCloseCreateReportDialog}
              onCancel={onCloseCreateReportDialog}
              onConfirm={onCloseCreateReportDialog}
              setReportData={setReportData}
            />
          )}
          <FaroIconButton
            size="s"
            margin={0}
            color={grey[0]}
            hoverColor={cyan[400]}
            aria-label="hide analysis"
            onClick={hideAnalysis}
            title="Hide analysis"
          >
            <CloseCancelErrorIcon />
          </FaroIconButton>
        </Stack>

        <Stack direction="row" spacing={2} sx={{ justifyContent: "center" }}>
          {/* Edit mode controls */}
          {editMode && (
            <Stack sx={{ gap: 1, width: "190px" }}>
              <ColormapReferencePlanePanel
                pointCloud={pointCloud}
                analysis={analysis}
                width="100%"
              />
              <ColormapOptionsForms
                pointCloud={pointCloud}
                analysis={analysis}
                width="100%"
              />
            </Stack>
          )}
          {/* Color scale */}
          <ColormapScale analysis={analysis} dark />
        </Stack>
      </Stack>
    </Box>
  );
}
